import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  FormHelperText,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { findShape } from '../../../../store/shape/api';
import { findFlavor } from '../../../../store/flavor/api';
import { findColor } from '../../../../store/color/api';
import { findToppingsCategoryItem } from '../../../../store/toppingsCategoryItem/api';
import { findToppingsCategory } from '../../../../store/toppingsCategory/api';
import { Shape } from '../../../../store/shape/api/interface';
import { Flavor } from '../../../../store/flavor/api/interface';
import { ToppingsCategoryItem } from '../../../../store/toppingsCategoryItem/api/interface';
import { mergeImages } from '../../../../utils/mergeImages';
import { getAssetURl } from '../../../../utils/url';
import { uploadAssets } from '../../../../utils/uploadAssets';
import { DocumentType } from '../../../../enum';
import { CakeBox } from '../../../../client/services';
import { MuiColorInput } from 'mui-color-input';

const CUSTOM_COLOR_OPTION = '__custom__';

const normalizeColorCode = (value: string): string => {
  const trimmed = String(value || '').trim().toLowerCase();
  if (!trimmed) {
    return '';
  }
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (/^#[0-9a-f]{3}$/.test(withHash)) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`;
  }
  return withHash;
};

const isPresetColorCode = (value: string, presetColors: { colorCode: string }[]): boolean => {
  const normalized = normalizeColorCode(value);
  if (!normalized) {
    return false;
  }
  return presetColors.some(
    (color) => normalizeColorCode(color.colorCode) === normalized
  );
};

export interface CustomCakeData {
  id?: string;
  shapeId: string;
  flavorId: string;
  colorCode: string;
  displayMode: 'front' | 'top';
  toppingId: string | null;
  printImageUrl: string | null; // Image to print on cake (adds 30 QAR)
  customImageUrl: string | null; // Custom image
  customImagePrice: number | null; // Price for custom image (admin only)
  categoryId: string | null;
  categoryCakeId: string | null;
  writeOnCake: string | null;
  instruction: string | null;
  cakeBoxId?: string | null;
  cakeBoxLabelEn?: string | null;
  cakeBoxPrice?: number | null;
  qty: number;
  shapeLabelEn: string;
  shapeLabelAr: string;
  flavorLabelEn: string;
  flavorLabelAr: string;
  toppingLabelEn?: string | null;
  toppingLabelAr?: string | null;
  unitPrice: number;
}

export const getSelectedCakeUnitPrice = (cake: CustomCakeData) =>
  Number(cake.unitPrice || 0) + Number(cake.cakeBoxPrice || 0);

export const getSelectedCakeLineTotal = (cake: CustomCakeData) =>
  getSelectedCakeUnitPrice(cake) * Number(cake.qty || 1);

export const getSelectedCakesTotal = (cakes: CustomCakeData[]) =>
  cakes.reduce((sum, cake) => sum + getSelectedCakeLineTotal(cake), 0);

interface CustomCakeBuilderProps {
  onCakeComplete: (cake: CustomCakeData) => void;
  editingCake?: CustomCakeData | null;
}

const CustomCakeBuilder: React.FC<CustomCakeBuilderProps> = ({ onCakeComplete, editingCake }) => {
  const dispatch = useAppDispatch();
  const { data: shapes } = useAppSelector((state) => state.shape);
  const { data: flavors } = useAppSelector((state) => state.flavor);
  const { data: colors } = useAppSelector((state) => state.color);
  const { data: toppingItems } = useAppSelector((state) => state.toppingsCategoryItem);
  const { data: toppingCategories } = useAppSelector((state) => state.toppingsCategory);
  const { accessToken, user } = useAppSelector((state) => state.auth);
  const userRole = user?.userRole;
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const [selectedShape, setSelectedShape] = useState<Shape | null>(null);
  const [selectedFlavor, setSelectedFlavor] = useState<Flavor | null>(null);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [colorSource, setColorSource] = useState<'none' | 'preset' | 'custom'>('none');
  const [displayMode, setDisplayMode] = useState<'front' | 'top'>('front');
  const [selectedToppingCategoryId, setSelectedToppingCategoryId] = useState<string>('');
  const [selectedTopping, setSelectedTopping] = useState<ToppingsCategoryItem | null>(null);
  const [customImageFile, setCustomImageFile] = useState<File | null>(null);
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [customImagePrice, setCustomImagePrice] = useState<number | null>(null);
  const [printImageFile, setPrintImageFile] = useState<File | null>(null);
  const [printImagePreview, setPrintImagePreview] = useState<string | null>(null);
  const [writeOnCake, setWriteOnCake] = useState<string>('');
  const [instruction, setInstruction] = useState<string>('');
  const [cakeBoxId, setCakeBoxId] = useState<string>('');
  const [cakeBoxes, setCakeBoxes] = useState<any[]>([]);
  const [qty, setQty] = useState<number>(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [mergedPreviewImage, setMergedPreviewImage] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingPrintImage, setIsUploadingPrintImage] = useState(false);
  const populatedEditingCakeIdRef = useRef<string | null>(null);
  const isPopulatingFromEditRef = useRef<boolean>(false);

  // Filter flavors by selected shape (flavors that have images for this shape)
  // When editing, always include the selected flavor even if it doesn't pass the filter
  const availableFlavors = selectedShape
    ? flavors.filter((flavor) => {
        // Always include the currently selected flavor (important for edit mode)
        if (selectedFlavor && flavor.id === selectedFlavor.id) {
          return true;
        }
        return flavor.shapeImages?.some((img) => img.shapeId === selectedShape.id);
      })
    : [];

  // Filter topping items by selected category and shape
  // Always include "Without" topping option (code: "without")
  const availableToppingItems = selectedShape
    ? toppingItems.filter((item) => {
        // Always include "Without" option
        if (item.code === 'without') return true;
        
        // Filter by category if one is selected
        if (selectedToppingCategoryId) {
          const category = toppingCategories.find((cat) => cat.id === selectedToppingCategoryId);
          const categoryItemIds = category?.topping_category_items?.map((catItem) => catItem.id) || [];
          if (!categoryItemIds.includes(item.id)) return false;
        }
        
        // Filter by shape - must have images for this shape
        return item.toppingShapeImages?.some((img) => img.shapeId === selectedShape.id);
      })
    : [];

  // Load initial data
  useEffect(() => {
    dispatch(findShape({ activeOnly: true, limit: 100 }));
    dispatch(findFlavor({ limit: 100 }));
    dispatch(findColor({ limit: 100 }));
    CakeBox.find({ query: { activeOnly: true, limit: 50, skip: 0 } })
      .then((result: any) => setCakeBoxes(result?.data ?? []))
      .catch(() => setCakeBoxes([]));
  }, [dispatch]);

  // Load topping categories when shape is selected
  useEffect(() => {
    if (selectedShape) {
      dispatch(
        findToppingsCategory({
          limit: 100,
        })
      );
      dispatch(
        findToppingsCategoryItem({
          limit: 100,
        })
      );
    }
  }, [dispatch, selectedShape]);

  // Reset dependent fields when shape changes (but not when populating from editingCake)
  useEffect(() => {
    if (selectedShape && !isPopulatingFromEditRef.current) {
      setSelectedFlavor(null);
      setSelectedToppingCategoryId('');
      setSelectedTopping(null);
    }
  }, [selectedShape]);

  // Reset topping when category changes
  useEffect(() => {
    if (selectedToppingCategoryId && !isPopulatingFromEditRef.current) {
      setSelectedTopping(null);
    }
  }, [selectedToppingCategoryId]);

  // Reset form when editingCake changes to null (only reset on editingCake change, not data changes)
  useEffect(() => {
    if (!editingCake) {
      // Only reset if we were previously editing (not on initial mount)
      if (populatedEditingCakeIdRef.current !== null) {
        setSelectedShape(null);
        setSelectedFlavor(null);
        setSelectedColor('');
        setColorSource('none');
        setDisplayMode('front');
        setSelectedToppingCategoryId('');
        setSelectedTopping(null);
        setCustomImageFile(null);
        setCustomImagePreview(null);
        setCustomImagePrice(null);
        setPrintImageFile(null);
        setPrintImagePreview(null);
        setWriteOnCake('');
        setInstruction('');
        setQty(1);
        setErrors({});
        populatedEditingCakeIdRef.current = null;
      }
    } else {
      // When editingCake changes to a different cake, clear the ref so the populate effect can run
      const editingCakeId = editingCake.id || `${editingCake.shapeId}-${editingCake.flavorId}`;
      if (populatedEditingCakeIdRef.current !== null && populatedEditingCakeIdRef.current !== editingCakeId) {
        populatedEditingCakeIdRef.current = null;
      }
    }
  }, [editingCake]);

  // Populate form when editingCake is provided and data is available
  useEffect(() => {
    if (editingCake && shapes.length > 0 && flavors.length > 0) {
      const editingCakeId = editingCake.id || `${editingCake.shapeId}-${editingCake.flavorId}`;
      
      // Only populate if we haven't already populated for this editingCake
      if (populatedEditingCakeIdRef.current !== editingCakeId) {
        isPopulatingFromEditRef.current = true;

        // Set all form fields synchronously to avoid race conditions
        const shape = shapes.find((s) => s.id === editingCake.shapeId);
        const flavor = flavors.find((f) => f.id === editingCake.flavorId);
        const topping = editingCake.toppingId 
          ? toppingItems.find((t) => t.id === editingCake.toppingId)
          : null;

        // Set shape first (this might trigger the reset effect, but ref prevents it)
        if (shape) {
          setSelectedShape(shape);
        }

        // Set flavor (will be set before reset effect clears it due to ref check)
        if (flavor) {
          setSelectedFlavor(flavor);
        }

        // Set topping category and topping (if exists)
        if (editingCake.toppingId && topping) {
          // Find the category for this topping
          const toppingCategory = toppingCategories.find((cat) =>
            cat.topping_category_items?.some((item) => item.id === topping.id)
          );
          if (toppingCategory) {
            setSelectedToppingCategoryId(toppingCategory.id);
          }
          setSelectedTopping(topping);
        } else {
          setSelectedToppingCategoryId('');
          setSelectedTopping(null);
        }

        // Set color (preset vs custom)
        const editColor = normalizeColorCode(editingCake.colorCode || '');
        if (!editColor) {
          setColorSource('none');
          setSelectedColor('');
        } else if (isPresetColorCode(editColor, colors)) {
          setColorSource('preset');
          setSelectedColor(editColor);
        } else {
          setColorSource('custom');
          setSelectedColor(editColor);
        }

        // Set display mode
        setDisplayMode(editingCake.displayMode);

        // Set custom image
        if (editingCake.customImageUrl) {
          setCustomImagePreview(editingCake.customImageUrl);
          setCustomImageFile(null);
          setCustomImagePrice(editingCake.customImagePrice || null);
        } else {
          setCustomImagePreview(null);
          setCustomImageFile(null);
          setCustomImagePrice(null);
        }

        // Set print image (adds 30 QAR)
        if (editingCake.printImageUrl) {
          setPrintImagePreview(editingCake.printImageUrl);
          setPrintImageFile(null);
        } else {
          setPrintImagePreview(null);
          setPrintImageFile(null);
        }

        // Set write on cake
        setWriteOnCake(editingCake.writeOnCake || '');

        // Set instructions
        setInstruction(editingCake.instruction || '');
        setCakeBoxId(editingCake.cakeBoxId || '');

        // Set quantity
        setQty(editingCake.qty || 1);

        // Clear errors
        setErrors({});
        
        populatedEditingCakeIdRef.current = editingCakeId;
        
        // Reset the flag after React has processed the state updates
        // Use a microtask to ensure it happens after the current effect cycle
        Promise.resolve().then(() => {
          isPopulatingFromEditRef.current = false;
        });
      }
    }
  }, [editingCake, shapes, flavors, toppingItems, colors, toppingCategories]);

  // Handle flavor when shape is set during edit (flavor needs shape to be available in dropdown)
  useEffect(() => {
    if (editingCake && editingCake.flavorId && selectedShape && flavors.length > 0 && !selectedFlavor) {
      const flavor = flavors.find((f) => f.id === editingCake.flavorId);
      if (flavor) {
        setSelectedFlavor(flavor);
      }
    }
  }, [editingCake, selectedShape, flavors, selectedFlavor]);

  // Handle topping when it loads after shape is selected during edit
  useEffect(() => {
    if (editingCake && editingCake.toppingId && selectedShape && toppingItems.length > 0 && !selectedTopping) {
      const topping = toppingItems.find((t) => t.id === editingCake.toppingId);
      if (topping) {
        // Find the category for this topping
        const toppingCategory = toppingCategories.find((cat) =>
          cat.topping_category_items?.some((item) => item.id === topping.id)
        );
        if (toppingCategory) {
          setSelectedToppingCategoryId(toppingCategory.id);
        }
        setSelectedTopping(topping);
      }
    }
  }, [editingCake, selectedShape, toppingItems, selectedTopping, toppingCategories]);

  // Generate merged preview image when shape, flavor, color, topping, or displayMode changes
  useEffect(() => {
    const generatePreview = async () => {
      if (!selectedShape || !selectedFlavor) {
        setMergedPreviewImage(null);
        return;
      }

      setIsGeneratingPreview(true);

      try {
        // Get the correct images based on displayMode
        const shapeImageUrl = displayMode === 'front' 
          ? selectedShape.frontImg 
          : selectedShape.topImg;
        
        const shapeColorImageUrl = displayMode === 'front'
          ? selectedShape.frontColorImg
          : selectedShape.topColorImg;

        // Find flavor image for selected shape
        const flavorShapeImage = selectedFlavor.shapeImages?.find(
          (img) => img.shapeId === selectedShape.id
        );
        
        const flavorImageUrl = displayMode === 'front'
          ? flavorShapeImage?.front
          : flavorShapeImage?.top;

        // Find topping image for selected shape
        let toppingImageUrl: string | undefined;
        if (selectedTopping) {
          const toppingShapeImage = selectedTopping.toppingShapeImages?.find(
            (img) => img.shapeId === selectedShape.id
          );
          toppingImageUrl = displayMode === 'front'
            ? toppingShapeImage?.front
            : toppingShapeImage?.top;
        }

        // Check if we have all required images
        // shapeColorImageUrl is only required if color is selected
        if (!shapeImageUrl || !flavorImageUrl || (selectedColor && !shapeColorImageUrl)) {
          setMergedPreviewImage(null);
          setIsGeneratingPreview(false);
          return;
        }

        // Merge images (skip color layer if no color is selected)
        const mergedImage = await mergeImages(
          getAssetURl(shapeImageUrl, assetUrl),
          getAssetURl(flavorImageUrl, assetUrl),
          selectedColor || '', // Pass empty string if no color - mergeImages will skip color layer
          toppingImageUrl ? getAssetURl(toppingImageUrl, assetUrl) : undefined,
          shapeColorImageUrl ? getAssetURl(shapeColorImageUrl, assetUrl) : ''
        );

        setMergedPreviewImage(mergedImage);
      } catch (error) {
        console.error('Error generating preview image:', error);
        setMergedPreviewImage(null);
      } finally {
        setIsGeneratingPreview(false);
      }
    };

    generatePreview();
  }, [selectedShape, selectedFlavor, selectedColor, selectedTopping, displayMode, assetUrl]);

  const handleShapeChange = (shapeId: string) => {
    const shape = shapes.find((s) => s.id === shapeId);
    setSelectedShape(shape || null);
    setErrors((prev) => ({ ...prev, shapeId: '' }));
  };

  const handleFlavorChange = (flavorId: string) => {
    const flavor = flavors.find((f) => f.id === flavorId);
    setSelectedFlavor(flavor || null);
    setErrors((prev) => ({ ...prev, flavorId: '' }));
  };

  const calculatePrice = (): number => {
    if (!selectedShape || !selectedFlavor) return 0;
    
    // Shape price (use discountedPrice if available and > 0, else use regular price)
    const shapeDiscountedPrice = parseFloat(selectedShape.discountedPrice) || 0;
    const shapePrice = shapeDiscountedPrice > 0 ? shapeDiscountedPrice : parseFloat(selectedShape.price) || 0;
    
    // Flavor price (pricePerKg * kg)
    const flavorPricePerKg = parseFloat(selectedFlavor.pricePerKg) || 0;
    const kg = parseFloat(selectedShape.kg) || 0;
    const flavorPrice = flavorPricePerKg * kg;
    
    // Topping price (use discountedPrice if available and > 0, else use regular price)
    let toppingPrice = 0;
    if (selectedTopping && selectedTopping.code !== 'without') {
      const toppingDiscountedPrice = parseFloat(selectedTopping.discountedPrice) || 0;
      toppingPrice = toppingDiscountedPrice > 0 ? toppingDiscountedPrice : parseFloat(selectedTopping.price) || 0;
    }
    
    // Printing charges (30 QAR if print image is uploaded - NOT for custom image)
    const printingCharges = printImagePreview ? 30 : 0;
    
    // Custom image price (if custom image is uploaded and price is set)
    const customImagePriceValue = (customImagePreview && customImagePrice !== null) ? customImagePrice : 0;
    
    // Unit price per cake = shape + flavor + topping + printing charges (if print image uploaded) + custom image price (if custom image uploaded)
    return shapePrice + flavorPrice + toppingPrice + printingCharges + customImagePriceValue;
  };

  const handleAddCake = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedShape) {
      newErrors.shapeId = 'Please select a shape';
    }
    if (!selectedFlavor) {
      newErrors.flavorId = 'Please select a flavor';
    }
    // Color is optional - no validation needed
    // Topping is optional, no validation needed
    // Custom image is optional, no validation needed

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // These should never be null at this point due to validation above, but TypeScript needs explicit checks
    if (!selectedShape || !selectedFlavor) {
      return;
    }

    // Upload custom image if provided (no price increase)
    let customImageUrl: string | null = null;
    if (customImageFile && accessToken) {
      setIsUploadingImage(true);
      try {
        const uploadResult: any = await uploadAssets(
          customImageFile,
          DocumentType.CakePrint,
          accessToken
        );
        if (uploadResult?.data?.keypath) {
          customImageUrl = getAssetURl(uploadResult.data.keypath, assetUrl);
        } else {
          console.error('Failed to upload custom image: No keypath in response');
          setErrors((prev) => ({ ...prev, customImage: 'Failed to upload custom image. Please try again.' }));
          setIsUploadingImage(false);
          return;
        }
      } catch (error) {
        console.error('Error uploading custom image:', error);
        setErrors((prev) => ({ ...prev, customImage: 'Failed to upload custom image. Please try again.' }));
        setIsUploadingImage(false);
        return;
      } finally {
        setIsUploadingImage(false);
      }
    } else if (customImagePreview && customImagePreview.startsWith('http')) {
      // If it's already a URL (from editing), use it as is
      customImageUrl = customImagePreview;
    }

    // Upload print image if provided (adds 30 QAR)
    let printImageUrl: string | null = null;
    if (printImageFile && accessToken) {
      setIsUploadingPrintImage(true);
      try {
        const uploadResult: any = await uploadAssets(
          printImageFile,
          DocumentType.CakePrint,
          accessToken
        );
        if (uploadResult?.data?.keypath) {
          printImageUrl = getAssetURl(uploadResult.data.keypath, assetUrl);
        } else {
          console.error('Failed to upload print image: No keypath in response');
          setErrors((prev) => ({ ...prev, printImage: 'Failed to upload print image. Please try again.' }));
          setIsUploadingPrintImage(false);
          return;
        }
      } catch (error) {
        console.error('Error uploading print image:', error);
        setErrors((prev) => ({ ...prev, printImage: 'Failed to upload print image. Please try again.' }));
        setIsUploadingPrintImage(false);
        return;
      } finally {
        setIsUploadingPrintImage(false);
      }
    } else if (printImagePreview && printImagePreview.startsWith('http')) {
      // If it's already a URL (from editing), use it as is
      printImageUrl = printImagePreview;
    }

    const cakeData: CustomCakeData = {
      shapeId: selectedShape.id,
      flavorId: selectedFlavor.id,
      colorCode: normalizeColorCode(selectedColor) || '',
      displayMode,
      toppingId: selectedTopping?.id || null, // Can be null if "Without" is selected or no topping
      printImageUrl: printImageUrl,
      customImageUrl: customImageUrl,
      customImagePrice: customImageUrl ? customImagePrice : null,
      categoryId: null,
      categoryCakeId: null,
      writeOnCake: writeOnCake.trim() || null,
      instruction: instruction.trim() || null,
      cakeBoxId: cakeBoxId || null,
      cakeBoxLabelEn: cakeBoxes.find((box) => box.id === cakeBoxId)?.labelEn || null,
      cakeBoxPrice: cakeBoxId
        ? Number(cakeBoxes.find((box) => box.id === cakeBoxId)?.price || 0)
        : null,
      qty,
      shapeLabelEn: selectedShape.labelEn,
      shapeLabelAr: selectedShape.labelAr,
      flavorLabelEn: selectedFlavor.labelEn,
      flavorLabelAr: selectedFlavor.labelAr,
      toppingLabelEn: selectedTopping ? (selectedTopping.code === 'without' ? 'Without' : selectedTopping.labelEn) : null,
      toppingLabelAr: selectedTopping ? (selectedTopping.code === 'without' ? 'بدون' : selectedTopping.labelAr) : null,
      unitPrice: calculatePrice(),
    };

    onCakeComplete(cakeData);
    
    // Reset form only if not editing (editing state is managed by parent)
    if (!editingCake) {
      setSelectedShape(null);
      setSelectedFlavor(null);
      setSelectedColor('');
      setColorSource('none');
      setDisplayMode('front');
      setSelectedTopping(null);
      setCustomImageFile(null);
      setCustomImagePreview(null);
      setCustomImagePrice(null);
      setPrintImageFile(null);
      setPrintImagePreview(null);
      setWriteOnCake('');
      setInstruction('');
      setCakeBoxId('');
      setQty(1);
      setErrors({});
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant='h6' gutterBottom>
        Build Custom Cake
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Shape Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.shapeId}>
            <InputLabel>Shape *</InputLabel>
            <Select
              value={selectedShape?.id || ''}
              onChange={(e) => handleShapeChange(e.target.value)}
              label='Shape *'
            >
              {shapes.map((shape) => (
                <MenuItem key={shape.id} value={shape.id}>
                  {shape.labelEn} ({shape.labelAr})
                </MenuItem>
              ))}
            </Select>
            {errors.shapeId && <FormHelperText>{errors.shapeId}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Flavor Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.flavorId} disabled={!selectedShape}>
            <InputLabel>Flavor *</InputLabel>
            <Select
              value={selectedFlavor?.id || ''}
              onChange={(e) => handleFlavorChange(e.target.value)}
              label='Flavor *'
            >
              {availableFlavors.map((flavor) => (
                <MenuItem key={flavor.id} value={flavor.id}>
                  {flavor.labelEn} ({flavor.labelAr})
                </MenuItem>
              ))}
            </Select>
            {errors.flavorId && <FormHelperText>{errors.flavorId}</FormHelperText>}
            {!selectedShape && (
              <FormHelperText>Please select a shape first</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Color Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.colorCode}>
            <InputLabel>Color (Optional)</InputLabel>
            <Select
              value={
                colorSource === 'none'
                  ? ''
                  : colorSource === 'custom'
                    ? CUSTOM_COLOR_OPTION
                    : selectedColor
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setColorSource('none');
                  setSelectedColor('');
                } else if (value === CUSTOM_COLOR_OPTION) {
                  setColorSource('custom');
                  if (
                    !selectedColor ||
                    isPresetColorCode(selectedColor, colors)
                  ) {
                    setSelectedColor('#ffffff');
                  }
                } else {
                  setColorSource('preset');
                  setSelectedColor(normalizeColorCode(value));
                }
                setErrors((prev) => ({ ...prev, colorCode: '' }));
              }}
              label='Color (Optional)'
            >
              <MenuItem value=''>
                <em>None (Optional)</em>
              </MenuItem>
              {colors.map((color) => (
                <MenuItem key={color.id} value={color.colorCode}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: color.colorCode,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                      }}
                    />
                    {color.colorCode}
                  </Box>
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM_COLOR_OPTION}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      background:
                        'linear-gradient(135deg, #ff0000 0%, #00ff00 50%, #0000ff 100%)',
                      border: '1px solid #ccc',
                      borderRadius: 1,
                    }}
                  />
                  Custom color
                </Box>
              </MenuItem>
            </Select>
            {errors.colorCode && <FormHelperText>{errors.colorCode}</FormHelperText>}
          </FormControl>
          {colorSource === 'custom' && (
            <Box sx={{ mt: 2 }}>
              <MuiColorInput
                label='Custom color'
                format='hex'
                value={selectedColor || '#ffffff'}
                onChange={(newColor) => {
                  setColorSource('custom');
                  setSelectedColor(normalizeColorCode(newColor));
                  setErrors((prev) => ({ ...prev, colorCode: '' }));
                }}
                fullWidth
                isAlphaHidden
              />
            </Box>
          )}
        </Grid>

        {/* Topping Category Selection */}
        {selectedShape && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Topping Category</InputLabel>
              <Select
                value={selectedToppingCategoryId}
                onChange={(e) => {
                  setSelectedToppingCategoryId(e.target.value);
                  setSelectedTopping(null); // Reset topping when category changes
                  setErrors((prev) => ({ ...prev, toppingId: '' }));
                }}
                label='Select Topping Category'
              >
                <MenuItem value=''>All Categories</MenuItem>
                {toppingCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.labelEn} ({category.labelAr})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Topping Selection Section */}
        {selectedShape && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.toppingId} disabled={!selectedShape}>
              <InputLabel>Select Topping</InputLabel>
              <Select
                value={selectedTopping?.id || ''}
                onChange={(e) => {
                  const topping = availableToppingItems.find((t) => t.id === e.target.value);
                  setSelectedTopping(topping || null);
                  setErrors((prev) => ({ ...prev, toppingId: '' }));
                }}
                label='Select Topping'
              >
                {availableToppingItems.length === 0 ? (
                  <MenuItem disabled>
                    {selectedToppingCategoryId
                      ? 'No toppings found in this category'
                      : 'Please select a topping category first'}
                  </MenuItem>
                ) : (
                  availableToppingItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.labelEn} ({item.labelAr})
                  </MenuItem>
                  ))
                )}
              </Select>
              {errors.toppingId && <FormHelperText>{errors.toppingId}</FormHelperText>}
              {!selectedShape && (
                <FormHelperText>Please select a shape first</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}

        {/* Display Mode */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant='body2' gutterBottom>
              Display Mode
            </Typography>
            <ToggleButtonGroup
              value={displayMode}
              exclusive
              onChange={(_, newMode) => newMode && setDisplayMode(newMode)}
              fullWidth
            >
              <ToggleButton value='front'>Front View</ToggleButton>
              <ToggleButton value='top'>Top View</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>

        {/* Preview Image - Below Display Mode */}
        {selectedShape && selectedFlavor && (
          <Grid item xs={12}>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              {isGeneratingPreview ? (
                <CircularProgress size={40} />
              ) : mergedPreviewImage ? (
                <Avatar
                  imgProps={{ crossOrigin: 'anonymous' }}
                  src={mergedPreviewImage}
                  sx={{
                    width: 200,
                    height: 200,
                    borderRadius: 'unset',
                    objectFit: 'contain',
                    '& img': { objectFit: 'contain' },
                    border: '1px solid #ddd',
                    bgcolor: 'background.paper',
                  }}
                  variant='square'
                />
              ) : (
                <Typography variant='body2' color='text.secondary' textAlign='center'>
                  Preview will appear when shape and flavor are selected
                </Typography>
              )}
            </Box>
          </Grid>
        )}

        {/* Custom Image Upload Section */}
        <Grid item xs={12}>
          <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 2, mt: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Custom Image (Optional)
            </Typography>
            <input
              accept='image/*'
              style={{ display: 'none' }}
              id='custom-image-upload'
              type='file'
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setCustomImageFile(file);
                  // Create preview
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setCustomImagePreview(reader.result as string);
                  };
                  reader.readAsDataURL(file);
                  setErrors((prev) => ({ ...prev, customImage: '' }));
                }
              }}
            />
            <label htmlFor='custom-image-upload'>
              <Button
                variant='outlined'
                component='span'
                sx={{
                  mb: customImagePreview ? 2 : 0,
                  borderColor: '#EE3750',
                  color: '#EE3750',
                  '&:hover': {
                    borderColor: '#EE3750',
                    backgroundColor: '#FDF0F1',
                  },
                }}
              >
                Choose Image File
              </Button>
            </label>
            {customImagePreview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={customImagePreview}
                  alt='Custom image preview'
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
                {customImageFile && (
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>
                    File: {customImageFile.name} ({(customImageFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
                <Button
                  size='small'
                  color='error'
                  onClick={() => {
                    setCustomImageFile(null);
                    setCustomImagePreview(null);
                    setCustomImagePrice(null);
                  }}
                  sx={{ mt: 1 }}
                >
                  Remove Image
                </Button>
              </Box>
            )}
            {/* Custom Image Price Field (Admin Only) */}
            {customImagePreview && (userRole === 'admin' || userRole === 'orderManager') && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  type='number'
                  label='Custom Image Price (QAR)'
                  value={customImagePrice !== null ? customImagePrice : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseFloat(e.target.value);
                    setCustomImagePrice(value !== null && !isNaN(value) && value >= 0 ? value : null);
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText='Enter price for custom image (will be added to cake price)'
                />
              </Box>
            )}
            {errors.customImage && (
              <FormHelperText error>{errors.customImage}</FormHelperText>
            )}
          </Box>
        </Grid>

        {/* Print Image Upload Section (adds 30 QAR) */}
        <Grid item xs={12}>
          <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 2, mt: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Print Image on Cake (Optional)
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
              This image will be printed on the cake (+30 QAR)
            </Typography>
            <input
              accept='image/*'
              style={{ display: 'none' }}
              id='print-image-upload'
              type='file'
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setPrintImageFile(file);
                  // Create preview
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setPrintImagePreview(reader.result as string);
                  };
                  reader.readAsDataURL(file);
                  setErrors((prev) => ({ ...prev, printImage: '' }));
                }
              }}
            />
            <label htmlFor='print-image-upload'>
              <Button
                variant='outlined'
                component='span'
                disabled={isUploadingPrintImage}
                sx={{
                  mb: printImagePreview ? 2 : 0,
                  borderColor: '#EE3750',
                  color: '#EE3750',
                  '&:hover': {
                    borderColor: '#EE3750',
                    backgroundColor: '#FDF0F1',
                  },
                }}
              >
                {isUploadingPrintImage ? 'Uploading...' : 'Choose Image File'}
              </Button>
            </label>
            {printImagePreview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={printImagePreview}
                  alt='Print image preview'
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
                {printImageFile && (
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 1 }}>
                    File: {printImageFile.name} ({(printImageFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
                <Button
                  size='small'
                  color='error'
                  onClick={() => {
                    setPrintImageFile(null);
                    setPrintImagePreview(null);
                  }}
                  sx={{ mt: 1 }}
                >
                  Remove Image
                </Button>
              </Box>
            )}
            {errors.printImage && (
              <FormHelperText error>{errors.printImage}</FormHelperText>
            )}
          </Box>
        </Grid>

        {/* Write on Cake */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Write on Cake (Optional)'
            value={writeOnCake}
            onChange={(e) => setWriteOnCake(e.target.value)}
            placeholder='e.g., Happy Birthday'
          />
        </Grid>

        {/* Instructions */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Instructions (Optional)'
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder='Special instructions for the cake'
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Premium Packaging (Optional)</InputLabel>
            <Select
              value={cakeBoxId}
              label='Premium Packaging (Optional)'
              onChange={(e) => setCakeBoxId(String(e.target.value))}
            >
              <MenuItem value=''>None</MenuItem>
              {cakeBoxes.map((box) => (
                <MenuItem key={box.id} value={box.id}>
                  {box.labelEn} (+ {Number(box.price || 0).toFixed(2)} QAR)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Quantity */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type='number'
            label='Quantity'
            value={qty}
            onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1 }}
          />
        </Grid>

        {/* Price Display */}
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 2, backgroundColor: '#FDF0F1', borderRadius: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Unit Price:
            </Typography>
            <Typography variant='h6'>{calculatePrice().toFixed(2)} QAR</Typography>
            <Typography variant='body2' color='text.secondary'>
              Total: {(calculatePrice() * qty).toFixed(2)} QAR
            </Typography>
          </Box>
        </Grid>

            {/* Error Messages for Images */}
        {errors.customImage && (
          <Grid item xs={12}>
            <FormHelperText error>{errors.customImage}</FormHelperText>
          </Grid>
        )}
        {errors.printImage && (
          <Grid item xs={12}>
            <FormHelperText error>{errors.printImage}</FormHelperText>
          </Grid>
        )}

        {/* Add Cake Button */}
        <Grid item xs={12}>
          <Button
            fullWidth
            variant='contained'
            onClick={handleAddCake}
            disabled={isUploadingImage || isUploadingPrintImage}
            sx={{
              backgroundColor: '#EE3750 !important',
              '&:hover': {
                backgroundColor: '#EE3750 !important',
              },
              '&:disabled': {
                backgroundColor: '#c4cdd5',
                color: '#fff',
              },
            }}
          >
            {isUploadingImage ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                Uploading Image...
              </>
            ) : (
              'Add Cake to Order'
            )}
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export default CustomCakeBuilder;

